import React from 'react';
import { Switch as HsdSwitch, Tooltip } from '@hopskipdrive/react-components';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import { TOOLTIP_HOVER_DELAY_MS } from '../constants';

export type OptionCardProps = {
  label: React.ReactNode;
  ariaLabel?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
  error?: boolean;
  errorMessage?: string;
};

export const OptionCard = ({
  label,
  ariaLabel,
  checked,
  onChange,
  disabled = false,
  tooltip,
  error = false,
  errorMessage,
}: OptionCardProps) => {
  const resolvedAriaLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined);
  const helperId = resolvedAriaLabel ? `option-card-helper-${resolvedAriaLabel}` : undefined;
  const showHelperText = error && Boolean(errorMessage);
  const card = (
    <Card
      $disabled={disabled}
      $checked={checked}
      $error={error}
      data-testid={resolvedAriaLabel ? `option-card-${resolvedAriaLabel}` : undefined}
    >
      <LabelText>{label}</LabelText>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => {
          if (disabled) {
            return;
          }
          onChange(next);
        }}
        inputProps={{ 'aria-label': resolvedAriaLabel, 'aria-describedby': showHelperText ? helperId : undefined }}
      />
    </Card>
  );

  const cardWithTooltip = tooltip ? (
    <Tooltip
      title={tooltip}
      enterDelay={TOOLTIP_HOVER_DELAY_MS}
      enterNextDelay={TOOLTIP_HOVER_DELAY_MS}
      enterTouchDelay={0}
      leaveTouchDelay={3000}
    >
      {card}
    </Tooltip>
  ) : (
    card
  );

  return (
    <Wrapper>
      {cardWithTooltip}
      {showHelperText && (
        <HelperText id={helperId} data-testid="option-card-helper-text">
          {errorMessage}
        </HelperText>
      )}
    </Wrapper>
  );
};

const mqDesktop = `@media (max-width: ${variables.breakpoints.desktop - 1}px)`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const getCardVariantStyles = ({ $checked, $error }: { $checked: boolean; $error: boolean }) => {
  if ($error) {
    return `
      border: 1px solid ${colorTokens.error.main};
      background-color: ${colorTokens.error.surface};
    `;
  }

  if ($checked) {
    return `
      border: 2px solid ${colorTokens.primary.main};
      background-color: ${colorTokens.primary.surface};
    `;
  }

  return `
    border: 1px solid ${colorTokens.border.default};
    background-color: ${colorTokens.background.default};
  `;
};

const Card = styled.label<{ $disabled: boolean; $checked: boolean; $error: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 320px;
  height: 56px;
  padding: 0 16px;
  border-radius: 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  font-family: inherit;
  ${getCardVariantStyles}
  ${mqDesktop} {
    width: 100%;
  }
`;

const LabelText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colorTokens.text.dark};
`;

const HelperText = styled.span`
  padding: 0 14px;
  font-size: 12px;
  color: ${colorTokens.error.dark};
`;

const Switch = styled(HsdSwitch)`
  &.MuiSwitch-root {
    width: 44px;
    height: 24px;
    padding: 0;
    overflow: visible;
  }
  & .MuiSwitch-track {
    opacity: 1;
    border-radius: 999px;
    background-color: ${colorTokens.action.active};
  }
  & .MuiSwitch-thumb {
    width: 16px;
    height: 16px;
    box-shadow: none;
  }
  & .MuiSwitch-switchBase {
    padding: 4px;
    color: ${colorTokens.background.default};
    &:hover {
      background-color: ${colorTokens.action.hover};
    }
    &.Mui-checked {
      transform: translateX(20px);
      color: ${colorTokens.background.default};
      & + .MuiSwitch-track {
        opacity: 1;
        background-color: ${colorTokens.primary.main};
      }
    }
    &.Mui-disabled + .MuiSwitch-track {
      opacity: 1;
      background-color: ${colorTokens.action.disabled};
    }
  }
`;
